import type { LibraryInstallResult } from '@/lib/api/generated/model';

import type { ItemRef } from './searchParams';

/**
 * What the last install of each item did, for as long as the reader is here
 * (spec 108 US1).
 *
 * Outside React on purpose. The report is produced by a mutation that lives in
 * `LibraryPage`, and the whole point of this store is the reader who follows one
 * of the report's own links: that unmounts the page and destroys the mutation.
 * Anything that survives the trip has to sit above the page or outside it.
 *
 * Not the query cache, which was the first thing tried. An entry written with
 * `setQueryData` and then left with no observer is collected after `gcTime` —
 * five minutes by default — and a reader who wanders off to read a file for six
 * minutes would come back to a silently empty drawer, which is the reported bug
 * with a longer fuse. Raising `gcTime` to `Infinity` for one key would be using
 * a cache as a store while switching off the thing that makes it a cache
 * (spec 108 research R1).
 *
 * The result is stored exactly as the service answered, including the blocked
 * form that names what this deployment lacks. That is what makes a restored
 * report identical to the first one by construction rather than by copying
 * fields across (FR-008).
 *
 * Nothing here expires. A report is written when an install answers, replaced
 * when the same item is installed again, dropped when its drawer is closed, and
 * gone when the tab is. That is the whole lifetime, and it is the one the spec
 * asks for.
 */

const reports = new Map<string, LibraryInstallResult>();
const listeners = new Set<() => void>();

function id(workspaceId: string, ref: ItemRef): string {
  return `${workspaceId}/${ref.kind}/${ref.key}`;
}

function announce() {
  for (const listener of listeners) listener();
}

export function getInstallReport(
  workspaceId: string,
  ref: ItemRef | undefined,
): LibraryInstallResult | undefined {
  if (!ref) return undefined;
  return reports.get(id(workspaceId, ref));
}

export function setInstallReport(
  workspaceId: string,
  ref: ItemRef,
  result: LibraryInstallResult,
): void {
  reports.set(id(workspaceId, ref), result);
  announce();
}

export function clearInstallReport(
  workspaceId: string,
  ref: ItemRef | undefined,
): void {
  if (!ref) return;
  // Only announce when something actually went, so closing a drawer that never
  // held a report does not re-render every reader of this store.
  if (reports.delete(id(workspaceId, ref))) announce();
}

/** For `useSyncExternalStore`; returns the unsubscribe it wants. */
export function subscribeToInstallReports(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Empty the store.
 *
 * Module state outlives a test file, so this runs from the shared setup beside
 * the MSW fixtures' own `resetLibraryState()`. Without it one test's install
 * would be another test's restored report.
 */
export function resetInstallReports(): void {
  reports.clear();
  announce();
}
